/**
 * Stage Gate Checklist Page
 * 
 * Kanban-style interface for tracking proposals through review stages.
 * Hidden for pilot deployment but accessible via direct URL.
 */

import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Card,
  Button,
  Select,
  Input,
  useWorkspace,
  PageHeader,
  PageContainer,
  listReports,
  type Report,
} from '@tracekit/core';
import { StageGateKanban } from '../components/StageGateKanban';
import { StageGateProposal, StageGateFilters, StageGateStatus } from '../types/stageGate';
import { Download, Filter, RefreshCw, X } from 'lucide-react';

export function StageGateChecklist() {
  const { projectId } = useWorkspace();
  const [filters, setFilters] = useState<StageGateFilters>({
    status: 'all',
    clientName: '',
    dateRange: undefined,
    reviewer: '',
  });

  // Fetch all reports (proposals)
  const { data: reports, isLoading, error, refetch } = useQuery({
    queryKey: ['stage-gate-reports', projectId],
    queryFn: async () => {
      if (!projectId) {
        throw new Error('Project ID is required');
      }
      const reportsList = await listReports(projectId);
      return reportsList;
    },
    enabled: !!projectId,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });

  // Transform reports to StageGateProposal format
  const proposals: StageGateProposal[] = useMemo(() => {
    if (!reports) return [];

    return reports
      .filter((report: Report) => {
        // Filter for RFP proposals
        const metadata = report.metadata || {};
        const isRfpProposal =
          report.report_type === 'custom' &&
          (metadata.report_type_override === 'rfp_proposal' || metadata.rfp_artifact_id);

        if (!isRfpProposal) return false;

        // Apply filters
        if (filters.status && filters.status !== 'all' && report.status !== filters.status) {
          return false;
        }

        if (filters.clientName) {
          const clientName = metadata.client_name || report.client_name || '';
          if (!clientName.toLowerCase().includes(filters.clientName.toLowerCase())) {
            return false;
          }
        }

        if (filters.dateRange?.start || filters.dateRange?.end) {
          const reportDate = new Date(report.created_at || report.updated_at || '');
          if (filters.dateRange.start) {
            const startDate = new Date(filters.dateRange.start);
            if (reportDate < startDate) return false;
          }
          if (filters.dateRange.end) {
            const endDate = new Date(filters.dateRange.end);
            endDate.setHours(23, 59, 59, 999);
            if (reportDate > endDate) return false;
          }
        }

        if (filters.reviewer) {
          const reviewer = metadata.current_reviewer || metadata.reviewer || '';
          if (!reviewer.toLowerCase().includes(filters.reviewer.toLowerCase())) {
            return false;
          }
        }

        return true;
      })
      .map((report: Report) => {
        const metadata = report.metadata || {};
        return {
          id: report.id,
          reportId: report.id,
          title: metadata.proposal_title || report.title || 'Untitled Proposal',
          clientName: metadata.client_name || report.client_name || 'Unknown Client',
          deadline: metadata.deadline || metadata.submission_deadline,
          status: (report.status as StageGateStatus) || 'draft',
          value: metadata.proposal_value || metadata.contract_value || 0,
          lastUpdated: report.updated_at || report.created_at || '',
          currentReviewer: metadata.current_reviewer || metadata.reviewer,
          commentsCount: metadata.comments_count || 0,
        };
      });
  }, [reports, filters]);

  const handleProposalClick = (reportId: string) => {
    window.location.href = `/reports/${reportId}`;
  };

  const handleExport = (format: 'csv' | 'json' | 'excel') => {
    // Simple export implementation
    const data = proposals.map((p) => ({
      title: p.title,
      client: p.clientName,
      status: p.status,
      value: p.value,
      deadline: p.deadline,
      reviewer: p.currentReviewer,
    }));

    if (format === 'json') {
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `stage-gate-checklist-${new Date().toISOString().split('T')[0]}.json`;
      a.click();
    } else if (format === 'csv') {
      const headers = ['Title', 'Client', 'Status', 'Value', 'Deadline', 'Reviewer'];
      const rows = data.map((d) => [
        d.title,
        d.client,
        d.status,
        d.value?.toString() || '',
        d.deadline || '',
        d.reviewer || '',
      ]);
      const csv = [headers, ...rows].map((row) => row.map((cell) => `"${cell}"`).join(',')).join('\n');
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `stage-gate-checklist-${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
    }
  };

  const clearFilters = () => {
    setFilters({
      status: 'all',
      clientName: '',
      dateRange: undefined,
      reviewer: '',
    });
  };

  if (isLoading) {
    return (
      <PageContainer>
        <PageHeader
          icon={<FileText />}
          title="Stage Gate Checklist"
          subtitle="Loading proposals..."
        />
      </PageContainer>
    );
  }

  if (error) {
    return (
      <PageContainer>
        <PageHeader
          icon={<FileText />}
          title="Stage Gate Checklist"
          subtitle="Error loading proposals"
        />
        <Card className="p-6 bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800">
          <p className="text-red-600 dark:text-red-400">Error: {(error as Error).message}</p>
        </Card>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageHeader
        icon={<FileText />}
        title="Stage Gate Checklist"
        subtitle="Track proposals through review stages"
        actions={
          <div className="flex gap-2">
            <div className="relative group">
              <Button variant="outline" size="sm" onClick={() => handleExport('csv')}>
                <Download className="w-4 h-4 mr-2" />
                Export
              </Button>
              <div className="hidden group-hover:block absolute top-full right-0 mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-10 min-w-[140px]">
                <button
                  onClick={() => handleExport('csv')}
                  className="block w-full text-left px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 text-sm"
                >
                  Export as CSV
                </button>
                <button
                  onClick={() => handleExport('json')}
                  className="block w-full text-left px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 text-sm"
                >
                  Export as JSON
                </button>
              </div>
            </div>
            <Button variant="outline" size="sm" onClick={() => refetch()}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>
        }
      />

      {/* Filters */}
      <Card className="mb-6 p-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <Filter className="w-5 h-5" />
            Filters
          </h3>
          {(filters.status !== 'all' ||
            filters.clientName ||
            filters.dateRange ||
            filters.reviewer) && (
            <Button variant="ghost" size="sm" onClick={clearFilters}>
              <X className="w-4 h-4 mr-1" />
              Clear
            </Button>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-theme-text mb-1">Status</label>
            <Select
              value={filters.status || 'all'}
              onChange={(e) =>
                setFilters({ ...filters, status: e.target.value as StageGateStatus | 'all' })
              }
            >
              <option value="all">All Statuses</option>
              <option value="draft">Draft</option>
              <option value="in_review">In Review</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
              <option value="archived">Archived</option>
            </Select>
          </div>

          <div>
            <label className="block text-sm font-medium text-theme-text mb-1">Client Name</label>
            <Input
              type="text"
              placeholder="Filter by client..."
              value={filters.clientName || ''}
              onChange={(e) => setFilters({ ...filters, clientName: e.target.value })}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-theme-text mb-1">Reviewer</label>
            <Input
              type="text"
              placeholder="Filter by reviewer..."
              value={filters.reviewer || ''}
              onChange={(e) => setFilters({ ...filters, reviewer: e.target.value })}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-theme-text mb-1">Date Range</label>
            <div className="flex gap-2">
              <Input
                type="date"
                placeholder="Start date"
                value={filters.dateRange?.start || ''}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    dateRange: { ...filters.dateRange, start: e.target.value },
                  })
                }
              />
              <Input
                type="date"
                placeholder="End date"
                value={filters.dateRange?.end || ''}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    dateRange: { ...filters.dateRange, end: e.target.value },
                  })
                }
              />
            </div>
          </div>
        </div>
      </Card>

      {/* Kanban Board */}
      <Card className="p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-theme-text flex items-center gap-2">
            <FileText className="w-6 h-6 text-blue-600" />
            Proposal Pipeline
          </h2>
          <div className="text-sm text-theme-text-secondary">
            {proposals.length} proposal{proposals.length !== 1 ? 's' : ''} total
          </div>
        </div>
        <StageGateKanban proposals={proposals} onProposalClick={handleProposalClick} />
      </Card>
    </PageContainer>
  );
}


